import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Edit3, Loader2, Trash2, Plus } from 'lucide-react'
import AppShell from '../../components/b2b/AppShell'
import DemoBadge from '../../components/b2b/DemoBadge'
import DemoBanner from '../../components/b2b/DemoBanner'
import { api, APIError } from '../../api/client'
import { getFitMeta } from '../../utils/b2bFit'

export default function JobDetailScreen() {
  const navigate = useNavigate()
  const { jobId } = useParams()
  const [job, setJob] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [tab, setTab] = useState('candidates')
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    let cancelled = false
    api.get(`/api/b2b/jobs/${jobId}`)
      .then((data) => { if (!cancelled) setJob(data) })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof APIError && err.status === 404) setError('Job not found.')
        else setError('Could not load job.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [jobId])

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await api.delete(`/api/b2b/jobs/${jobId}`)
      navigate('/b2b/jobs', { replace: true })
    } catch {
      setError('Could not delete job.')
      setDeleting(false)
      setDeleteConfirm(false)
    }
  }

  if (loading) {
    return (
      <AppShell>
        <div className="px-6 md:px-10 py-12 max-w-4xl mx-auto">
          <p className="text-sm text-ink-3">Loading…</p>
        </div>
      </AppShell>
    )
  }

  if (error || !job) {
    return (
      <AppShell>
        <div className="px-6 md:px-10 py-12 max-w-4xl mx-auto">
          <Link to="/b2b/jobs" className="inline-flex items-center gap-2 text-sm text-ink-3 hover:text-ink-1">
            <ArrowLeft className="w-4 h-4" />
            Back to jobs
          </Link>
          <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error || 'Job not found.'}
          </div>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-4xl mx-auto">
        <Link to="/b2b/jobs" className="inline-flex items-center gap-2 text-sm text-ink-3 hover:text-ink-1">
          <ArrowLeft className="w-4 h-4" />
          Back to jobs
        </Link>

        <div className="mt-4 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-bold text-ink-1">{job.title}</h1>
              {job.is_demo && <DemoBadge />}
            </div>
            <p className="text-sm text-ink-3 mt-1">
              {[job.department, job.location].filter(Boolean).join(' • ') || 'No details yet'}
              <span className="ml-2 inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-canvas">
                {job.status}
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/b2b/jobs/${job.id}/edit`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-semibold text-ink-2 hover:bg-canvas border border-black/10 transition"
            >
              <Edit3 className="w-4 h-4" />
              Edit
            </Link>
            <button
              type="button"
              onClick={() => setDeleteConfirm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-semibold text-red-600 hover:bg-red-50 border border-red-100 transition"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>

        {job.is_demo && (
          <div className="mt-6">
            <DemoBanner>
              <strong>This is a sample job</strong> we added so you can explore the screening flow without setting up your own. Try opening the candidate, or delete this job when you're ready.
            </DemoBanner>
          </div>
        )}

        <div className="mt-8 border-b border-black/5">
          <div className="flex gap-6">
            {['candidates', 'brief'].map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={`pb-3 text-sm font-semibold capitalize border-b-2 transition ${
                  tab === id
                    ? 'text-ink-1 border-accent'
                    : 'text-ink-3 border-transparent hover:text-ink-2'
                }`}
              >
                {id}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          {tab === 'candidates' ? (
            <CandidatesPanel jobId={job.id} />
          ) : (
            <BriefPanel job={job} />
          )}
        </div>
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-50">
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-sm w-full">
            <h2 className="text-lg font-bold text-ink-1">Delete this job?</h2>
            <p className="mt-2 text-sm text-ink-3">
              This will permanently delete the job and any attached candidate links.
              Candidates themselves are kept.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 rounded-2xl text-sm font-semibold text-ink-2 hover:bg-canvas transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 rounded-2xl text-sm font-bold text-white bg-red-600 hover:bg-red-700 transition disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  )
}

function CandidatesPanel({ jobId }) {
  const [candidates, setCandidates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [sort, setSort] = useState('created_desc')

  // Auto-poll while any candidate has fit_status='processing' so the verdict
  // badge appears as soon as the agent finishes (without page refresh).
  useEffect(() => {
    let cancelled = false
    let timer = null

    const fetchOnce = () => {
      api.get(`/api/b2b/jobs/${jobId}/candidates?sort=${encodeURIComponent(sort)}`)
        .then((data) => {
          if (cancelled) return
          setCandidates(data)
          const stillProcessing = (data || []).some(
            (c) => c.fit_status === 'pending' || c.fit_status === 'processing'
          )
          if (stillProcessing) {
            timer = setTimeout(fetchOnce, 5000)
          }
        })
        .catch((err) => {
          if (cancelled) return
          if (err instanceof APIError && err.status >= 500) setError('Could not load candidates.')
          else if (!(err instanceof APIError)) setError('Network error.')
        })
        .finally(() => { if (!cancelled) setLoading(false) })
    }

    fetchOnce()
    return () => { cancelled = true; if (timer) clearTimeout(timer) }
  }, [jobId, sort])

  if (loading) {
    return <p className="text-sm text-ink-3">Loading candidates…</p>
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
        {error}
      </div>
    )
  }

  if (candidates.length === 0) {
    return (
      <div className="bg-white rounded-3xl shadow-card p-8 text-center">
        <p className="text-sm text-ink-3 max-w-md mx-auto">
          No candidates yet. Add the first one — paste their resume into structured sections.
        </p>
        <Link
          to={`/b2b/jobs/${jobId}/candidates/new`}
          className="mt-6 inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-5 py-2.5 rounded-2xl transition"
        >
          <Plus className="w-4 h-4" />
          Add candidate
        </Link>
      </div>
    )
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <label className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink-3">
          Sort
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-white border border-black/10 rounded-xl px-3 py-1.5 text-xs font-semibold text-ink-1 focus:outline-none focus:border-accent"
          >
            <option value="created_desc">Newest first</option>
            <option value="created_asc">Oldest first</option>
            <option value="fit_desc">Best fit first</option>
            <option value="fit_asc">Worst fit first</option>
          </select>
        </label>
        <Link
          to={`/b2b/jobs/${jobId}/candidates/new`}
          className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-4 py-2 rounded-2xl transition text-sm"
        >
          <Plus className="w-4 h-4" />
          Add candidate
        </Link>
      </div>
      <div className="bg-white rounded-3xl shadow-card overflow-hidden">
        {candidates.map((c, idx) => (
          <Link
            key={c.id}
            to={`/b2b/jobs/${jobId}/candidates/${c.id}`}
            className={`flex items-center gap-4 px-5 py-4 hover:bg-canvas transition ${
              idx > 0 ? 'border-t border-black/5' : ''
            }`}
          >
            <div className="w-9 h-9 rounded-full bg-accent/10 flex items-center justify-center text-accent text-xs font-bold">
              {(c.name || '?').split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('')}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-bold text-ink-1 truncate">{c.name}</p>
                {c.is_demo && <DemoBadge />}
              </div>
              <p className="text-xs text-ink-3 truncate">{c.email || 'No email'}</p>
            </div>
            <FitVerdictBadge verdict={c.fit_verdict} status={c.fit_status} />
            <ScreeningProgressPill candidate={c} />
          </Link>
        ))}
      </div>
    </div>
  )
}

function ScreeningProgressPill({ candidate }) {
  const s = candidate.screening_status
  const ans = candidate.screening_answered_count || 0
  const total = candidate.screening_total_count || 0

  if (!candidate.has_screening || !s) {
    return (
      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
        No screening
      </span>
    )
  }
  if (s === 'completed') {
    return (
      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
        Submitted
      </span>
    )
  }
  if (s === 'declined') {
    return (
      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500" title="Candidate opted out">
        Declined
      </span>
    )
  }
  if (s === 'expired') {
    return (
      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 italic">
        Expired
      </span>
    )
  }
  if (s === 'in_progress') {
    return (
      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
        In progress {ans}/{total}
      </span>
    )
  }
  if (s === 'identity_verified') {
    return (
      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700" title="Candidate confirmed email; not yet started">
        Email verified
      </span>
    )
  }
  // 'sent' or 'draft'
  return (
    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
      Sent
    </span>
  )
}

function FitVerdictBadge({ verdict, status }) {
  if (status === 'pending' || status === 'processing') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border bg-gray-50 text-gray-500 border-gray-200">
        <Loader2 className="w-3 h-3 animate-spin" />
        Analyzing
      </span>
    )
  }
  if (status === 'failed') {
    return (
      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border bg-red-50 text-red-600 border-red-200" title="Fit analysis failed — open candidate to retry.">
        Failed
      </span>
    )
  }
  const meta = getFitMeta(verdict)
  if (!meta) return null
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full border ${meta.badgeClass}`}
      title={meta.description}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dotClass}`} />
      {meta.label}
    </span>
  )
}

function BriefPanel({ job }) {
  const blocks = job.blocks || []
  if (blocks.length === 0) {
    return (
      <div className="bg-white rounded-3xl shadow-card p-8 text-center">
        <p className="text-sm text-ink-3">No brief yet.</p>
        <Link
          to={`/b2b/jobs/${job.id}/edit`}
          className="mt-4 inline-flex items-center gap-2 text-accent font-bold hover:underline text-sm"
        >
          <Edit3 className="w-4 h-4" />
          Add sections
        </Link>
      </div>
    )
  }
  return (
    <div className="space-y-3">
      {blocks.map((block) => (
        <div key={block.id} className="bg-white rounded-2xl shadow-card p-5">
          <h3 className="text-xs font-bold uppercase tracking-wider text-ink-3">{block.label}</h3>
          <p className="mt-2 text-[14px] text-ink-1 whitespace-pre-wrap">{block.content || '—'}</p>
        </div>
      ))}
    </div>
  )
}
